import React, { useRef, useState, useImperativeHandle, forwardRef } from 'react';
import classNames from 'classnames';
import SvgArrow from '../../../../components/CreateMeetingFooter/SectionMenu/SvgArrow';
import footerClasses from '../../../../components/CreateMeetingFooter/styles.module.scss';
import sectionMenuClasses from '../../../../components/CreateMeetingFooter/SectionMenu/styles.module.scss';
import Tooltip from '../../../../components/Tooltip';
import TimelineSectionCard from '../TimelineSectionCard';
import TimelineGap from '../TimelineGap';

const SECTION_WIDTH = 235;
const GAP = 50;

const FooterTimeline = forwardRef(({
  sectionsListContainerRef,
  sectionsListRef,
  sectionsCount,
  currentSectionsPage,
  sectionsPageCount,
  sectionsListTranslateX,
  scrollSections,
  handleDragOver,
  handleSectionDrop,
  handleDragEnd,
  timelineItems,
  allAssets,
  isPreviewMode,
  plusSignRef,
  addSectionContainerRef,
  isMenuVisible,
  setMenuVisible,
  selectItem,
  currentItem,
  draggedItemId,
  dragOverIndex,
  focusedSectionTitleId,
  hoveredSectionTitleId,
  isEditMode,
  handleSectionTitleChange,
  handleOpenEditQuestion,
  handleOpenEditText,
  handleOpenEditPlaceholder,
  handleDragStart,
  isItemAllowedToDrag,
  removeTimelineItem,
  user,
  fetchedSections,
  isCreator = true,
  completedSectionIds = new Set(),
  isTrainingView = false,
  isTemplateMode = false,
  onAddPlaceholder,
  onPlaceholderAddContent,
  className,
  style,
}, ref) => {
  // Timeline-specific state - moved from parent
  const toggleRef = useRef();
  const gapHoverTimerRef = useRef();
  const [hoveredItemId, setHoveredItemId] = useState(null);
  const [hoveredGapAfterItemId, setHoveredGapAfterItemId] = useState(null);
  const [pendingSaveItemIds, setPendingSaveItemIds] = useState(() => new Set());
  const [savedPopup, setSavedPopup] = useState({ itemId: null, visible: false });

  // Expose pendingSaveItemIds for publish/save functions
  useImperativeHandle(ref, () => ({
    getPendingSaveItemIds: () => pendingSaveItemIds,
    clearPendingSaveItemIds: () => setPendingSaveItemIds(new Set()),
  }));
  return (
    <footer
      className={classNames(footerClasses.CreateMeetingFooter, className)}
      style={style}
    >
      <div className={footerClasses.footerContainer}>
        <div ref={sectionsListContainerRef} className={footerClasses.sectionsListContainer}>
          <div
            className={`${footerClasses.switch} ${footerClasses.switchLeft}`}
            onClick={() => scrollSections('right')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                scrollSections('right');
              }
            }}
          >
            <SvgArrow />
          </div>
          <div
            className={`${footerClasses.switch} ${footerClasses.switchRight}`}
            onClick={() => scrollSections('left')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                scrollSections('left');
              }
            }}
          >
            <SvgArrow />
          </div>
          {/* Minimal structure to anchor SectionMenu like the real editor */}
          <ul 
            ref={sectionsListRef}
            className={footerClasses.sectionsList} 
            onDragOver={handleDragOver}
            onDrop={handleSectionDrop}
            onDragLeave={handleDragEnd}
            style={{ 
              width: (SECTION_WIDTH + GAP) * sectionsCount, 
              ...(() => {
                const isFirstPage = currentSectionsPage === 0;
                const isLastPage = currentSectionsPage >= sectionsPageCount;
                
                let gradient = 'linear-gradient(to right';
                if (isFirstPage && isLastPage) {
                  // No fade needed on either side
                  gradient += ', black 0px, black 100%';
                } else if (isFirstPage) {
                  // No fade on left, fade on right
                  gradient += ', black 0px, black calc(100% - 100px), transparent 100%';
                } else if (isLastPage) {
                  // Fade on left, no fade on right
                  gradient += ', transparent 0px, black 100px, black 100%';
                } else {
                  // Fade on both sides
                  gradient += ', transparent 0px, black 100px, black calc(100% - 100px), transparent 100%';
                }
                gradient += ')';
                
                return {
                  maskImage: gradient,
                  WebkitMaskImage: gradient,
                };
              })(),
            }}
          >
            <div className={footerClasses.inner} style={{ transform: `translateX(${sectionsListTranslateX}px)` }}>
              {timelineItems.map((item, itemIndex) => {
                const asset = allAssets.find((a) => a.id === item.asset_id);
                if (!asset) return null;
                const isHovered = hoveredItemId === item.id;
                const isVideo = asset.type === 'video';
                const isLastItem = itemIndex === timelineItems.length - 1;
                const isGapHovered = hoveredGapAfterItemId === item.id;
                const isDragged = draggedItemId === item.id;
                const isDragOverBefore = dragOverIndex === itemIndex;
                const isDragOverAfter = dragOverIndex === itemIndex + 1;
                const isAllowedToDrag = isItemAllowedToDrag(item);
                
                const sectionId = item.metadata?.sectionId;
                const isCompleted = sectionId ? completedSectionIds.has(sectionId) : false;
                
                return (
                  <React.Fragment key={`item-${item.id}`}>
                    <TimelineSectionCard
                      item={item}
                      itemIndex={itemIndex}
                      asset={asset}
                      currentItem={currentItem}
                      isHovered={isHovered}
                      isVideo={isVideo}
                      isDragged={isDragged}
                      isDragOverBefore={isDragOverBefore}
                      isDragOverAfter={isDragOverAfter}
                      isAllowedToDrag={isAllowedToDrag}
                      isPreviewMode={isPreviewMode}
                      isEditMode={isEditMode}
                      pendingSaveItemIds={pendingSaveItemIds}
                      savedPopup={savedPopup}
                      focusedSectionTitleId={focusedSectionTitleId}
                      hoveredSectionTitleId={hoveredSectionTitleId}
                      handleSectionTitleChange={handleSectionTitleChange}
                      handleOpenEditQuestion={handleOpenEditQuestion}
                      handleOpenEditText={handleOpenEditText}
                      handleOpenEditPlaceholder={handleOpenEditPlaceholder}
                      handleDragStart={handleDragStart}
                      handleDragEnd={handleDragEnd}
                      setHoveredItemId={setHoveredItemId}
                      setPendingSaveItemIds={setPendingSaveItemIds}
                      setSavedPopup={setSavedPopup}
                      removeTimelineItem={removeTimelineItem}
                      user={user}
                      fetchedSections={fetchedSections}
                      isCreator={isCreator}
                      isCompleted={isCompleted}
                      isTrainingView={isTrainingView}
                      isTemplateMode={isTemplateMode}
                      onPlaceholderAddContent={onPlaceholderAddContent}
                    />
                    {!isLastItem && (
                      <TimelineGap
                        item={item}
                        isPreviewMode={isPreviewMode}
                        isTemplateMode={isTemplateMode}
                        isGapHovered={isGapHovered}
                        gapHoverTimerRef={gapHoverTimerRef}
                        plusSignRef={plusSignRef}
                        setHoveredGapAfterItemId={setHoveredGapAfterItemId}
                      />
                    )}
                  </React.Fragment>
                );
              })}

              {!isPreviewMode && (
                <li
                  style={{
                    display: 'inline-block',
                    marginLeft: isTemplateMode ? 24 : 0,
                    marginRight: 40,
                  }}
                >
                  <div ref={addSectionContainerRef} className={footerClasses.sectionMenuContainer}>
                    <div
                      ref={toggleRef}
                      className={sectionMenuClasses.SectionMenu}
                      onClick={(e) => {
                        if (isTemplateMode && onAddPlaceholder) {
                          onAddPlaceholder();
                          return;
                        }
                        // Set ref to this element before opening menu
                        const svgElement = e.currentTarget.querySelector('svg');
                        if (svgElement) {
                          // eslint-disable-next-line no-param-reassign
                          plusSignRef.current = svgElement;
                        }
                        // Always append: select last item (or null if empty)
                        const lastId = timelineItems[timelineItems.length - 1]?.id || null;
                        if (lastId) {
                          selectItem(lastId);
                        }
                        setMenuVisible(!isMenuVisible);
                      }}
                    >
                      <div className={sectionMenuClasses.toggle}>
                        <Tooltip
                          width={200}
                          position="top-right"
                          text={
                            isTemplateMode
                              ? 'Add a placeholder section to your template'
                              : 'Choose different ways to record content for your video'
                          }
                        >
                          <svg
                            width="34"
                            height="33"
                            viewBox="0 0 34 33"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M12.8361 0V12.1291H0.707031V20.2151H12.8361V32.3442H20.9222V20.2151H33.0513V12.1291H20.9222V0H12.8361Z"
                              fill="#F8585C"
                            />
                          </svg>
                        </Tooltip>
                        <span>{isTemplateMode ? 'Add Placeholder' : 'Add Section'}</span>
                      </div>
                    </div>
                  </div>
                </li>
              )}
            </div>
          </ul>
        </div>
      </div>
    </footer>
  );
});

FooterTimeline.displayName = 'FooterTimeline';

export default FooterTimeline;
